import React, { useContext } from "react";
import { Box, CircularProgress, Typography, IconButton, Tooltip, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import EditorContext from "../../../../contexts/EditorContext";
import { normalizeText, splitIntoParagraphs } from "../../DocumentEditor/extensions/CustomHighlight";
import ReactGA from 'react-ga4';
import { tracker } from '../../../../analytics';

const DetailsCard = ({ details, suggestion, errorText, accentColor, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useTranslation();
  const editorRef = useContext(EditorContext);
  const contract = useSelector(state => state?.contract?.contract);
  const currentUser = useSelector(state => state.auth?.user?.user);
  const contractStatus = contract?.status;
  const userRole = (contract?.collaborators || []).find(c => c.id === currentUser?.id)?.role;
  const isLocked = ['PENDING_SIGNATURE', 'SIGNED'].includes(contractStatus) || userRole === 'reviewer';

  const handleAddSuggestion = () => {
    if (isLocked || !suggestion || !errorText) return;
    const editor = editorRef?.current?.getEditor();
    if (!editor) return;

    ReactGA.event({ category: 'RiskAnalysis', action: 'Apply Suggestion', label: suggestion.slice(0, 60) });
    tracker.event({ category: 'RiskAnalysis', action: 'Apply Suggestion', label: suggestion.slice(0, 60) });

    try {
      editor.commands.clearRiskHighlight();
      const { state } = editor.view;
      const { doc } = state;
      const content = editor.getHTML();
      const paragraphs = splitIntoParagraphs(errorText);

      if (paragraphs.length > 0) {
        if (paragraphs.length > 1) {
          const result = handleMultiParagraphReplacement(editor, paragraphs, suggestion);
          if (result) { if (onClose) onClose(); return; }
        }

        const matchPositions = [];
        for (const paragraph of paragraphs) {
          if (paragraph.length < 10) continue;
          const normalizedSearchText = normalizeText(paragraph);
          const searchRegex = new RegExp(escapeRegExp(normalizedSearchText), 'i');
          doc.descendants((node, pos) => {
            if (node.isText) {
              const normalizedNodeText = normalizeText(node.text);
              if (searchRegex.test(normalizedNodeText)) {
                const match = normalizedNodeText.match(searchRegex);
                if (match && match.index !== undefined) {
                  matchPositions.push({ from: pos + match.index, to: pos + match.index + match[0].length, paragraph });
                }
              }
            }
            return true;
          });
        }

        if (matchPositions.length > 0) {
          matchPositions.sort((a, b) => b.from - a.from);
          let tr = state.tr;
          for (const { from, to } of matchPositions) {
            if (paragraphs.length === 1 && matchPositions.length > 1) tr.deleteRange(from, to).insertText(suggestion, from);
            else { tr.deleteRange(from, to).insertText(suggestion, from); break; }
          }
          editor.view.dispatch(tr);
          if (onClose) onClose();
          return;
        }
      }

      let replacementMade = false;
      if (paragraphs.length > 1) {
        const normalizedFullText = normalizeText(errorText.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, ''));
        if (content.includes(normalizedFullText)) {
          editor.commands.setContent(content.replace(normalizedFullText, suggestion));
          replacementMade = true;
        }
      }
      if (!replacementMade) {
        for (const paragraph of paragraphs) {
          const normalizedPara = normalizeText(paragraph);
          if (content.includes(normalizedPara)) {
            editor.commands.setContent(content.replace(normalizedPara, suggestion));
            replacementMade = true;
            break;
          }
        }
      }
      if (replacementMade) { if (onClose) onClose(); return; }

      const normalizedErrorText = normalizeText(errorText.replace(/<[^>]*>/g, ''));
      const fuzzyResult = fuzzyReplace(content, normalizedErrorText, suggestion);
      if (fuzzyResult) { editor.commands.setContent(fuzzyResult); if (onClose) onClose(); }
    } catch (err) {
      console.error("Error replacing text:", err);
    }
  };

  const handleMultiParagraphReplacement = (editor, paragraphs, suggestion) => {
    try {
      const { state } = editor.view;
      const { doc } = state;
      const paragraphPositions = [];

      for (const paragraph of paragraphs) {
        if (paragraph.length < 10) continue;
        const positions = [];
        const normalizedSearchText = normalizeText(paragraph);
        const searchRegex = new RegExp(escapeRegExp(normalizedSearchText), 'i');
        doc.descendants((node, pos) => {
          if (node.isText) {
            const normalizedNodeText = normalizeText(node.text);
            if (searchRegex.test(normalizedNodeText)) {
              const match = normalizedNodeText.match(searchRegex);
              if (match && match.index !== undefined) {
                positions.push({ from: pos + match.index, to: pos + match.index + match[0].length });
              }
            }
          }
          return true;
        });
        paragraphPositions.push(positions);
      }

      if (paragraphPositions.some(p => p.length === 0)) return false;

      let rangeStart = null, rangeEnd = null;
      if (paragraphPositions.length >= 2) {
        for (const pos1 of paragraphPositions[0]) {
          for (const pos2 of paragraphPositions[1]) {
            if (pos2.from - pos1.to > 0 && pos2.from - pos1.to < 50) {
              rangeStart = pos1.from; rangeEnd = pos2.to; break;
            }
          }
          if (rangeStart !== null) break;
        }
      }

      if (rangeStart !== null && rangeEnd !== null) {
        const tr = state.tr;
        tr.deleteRange(rangeStart, rangeEnd).insertText(suggestion, rangeStart);
        editor.view.dispatch(tr);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Error in multi-paragraph replacement:", err);
      return false;
    }
  };

  const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const fuzzyReplace = (content, searchText, replacement) => {
    if (searchText.length < 20) return null;
    const reducedText = searchText.substring(0, Math.floor(searchText.length * 0.8));
    return content.includes(reducedText) ? content.replace(reducedText, replacement) : null;
  };

  const borderColor = accentColor ? alpha(accentColor, 0.25) : alpha(theme.palette.divider, 0.7);

  return (
    <Box sx={{
      borderRadius: '10px',
      border: `1px solid ${borderColor}`,
      backgroundColor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.015),
      overflow: 'hidden',
      mb: 0.75,
    }}>
      {/* Details section */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t("riskAnalysis.detailsCard.details")}
          </Typography>
        </Box>
        {details ? (
          <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.6, color: theme.palette.text.primary, textAlign: 'left' }}>
            {details}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={12} />
            <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary }}>{t("riskAnalysis.generatingObservations")}</Typography>
          </Box>
        )}
      </Box>

      {/* Suggestion section */}
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: theme.palette.success.main, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t("riskAnalysis.detailsCard.suggestion")}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={t("riskAnalysis.detailsCard.add")}>
              <span>
                <IconButton
                  size="small"
                  disabled={!suggestion || isLocked}
                  onClick={handleAddSuggestion}
                  sx={{
                    width: 26, height: 26, borderRadius: '7px',
                    backgroundColor: suggestion ? alpha(theme.palette.success.main, 0.12) : 'transparent',
                    color: suggestion ? theme.palette.success.main : theme.palette.text.disabled,
                    border: `1px solid ${suggestion ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.divider, 0.5)}`,
                    '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.2) },
                    '&.Mui-disabled': { opacity: 0.35 },
                  }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Fermer">
              <IconButton
                size="small"
                onClick={onClose}
                sx={{
                  width: 26, height: 26, borderRadius: '7px',
                  color: theme.palette.text.disabled,
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  '&:hover': { color: theme.palette.text.secondary },
                }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {suggestion ? (
          <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.6, color: theme.palette.text.primary, textAlign: 'left' }}>
            {suggestion}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={12} />
            <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary }}>{t("riskAnalysis.generatingSuggestions")}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DetailsCard;
